<template>
  <ul class="flex flex-wrap gap-x-3 gap-y-6 my-8 max-w-[636px] mx-auto">
    <li
      v-for="(file, index) in files"
      :key="file.file.id"
      class="w-[150px] flex flex-col gap-2"
    >
      <div
        class="group relative w-[150px] h-[150px] rounded-lg border shrink-0"
        :draggable="isFileEditable && filesCount > 1"
        @dragstart="dragIndex = index"
        @dragover.prevent
        @drop.prevent="onDrop(index)"
        @dragend="dragIndex = null"
      >
        <video
          v-if="isFileVideo(file.file.url)"
          :src="file.file.url"
          class="w-full h-full object-cover rounded-lg pointer-events-none"
          muted
          playsinline
        />
        <img
          v-else
          :src="file.file.url"
          alt=""
          class="w-full h-full object-cover rounded-lg pointer-events-none"
        />

        <span
          v-if="isFileEditable && filesCount > 1"
          class="absolute top-2 right-2 hidden group-hover:flex items-center bg-white rounded-md p-1 shadow-md cursor-grab"
        >
          <Icon :size="IconSize.LG" :name="IconName.DRAG" />
        </span>

        <button
          v-if="isFileEditable"
          type="button"
          class="absolute bottom-2 right-2 hidden group-hover:flex items-center bg-white rounded-md p-1 shadow-md"
          @click.stop="deleteFile(file.file.id)"
        >
          <Icon :size="IconSize.LG" :name="IconName.DELETE" />
        </button>
      </div>

      <TextArea
        v-if="!noCaption && filesCount > 1"
        :model-value="file.caption ?? ''"
        :label="$t('social-post-files-modal.caption')"
        :placeholder="$t('social-post-files-modal.caption')"
        :rules="file.caption ? [maxLengthLimit] : undefined"
        class="w-full"
        @update:model-value="file.caption = $event || null"
      />
      <p v-else-if="!noCaption" class="text-sm text-secondary">
        {{ $t('social-post-files-modal.single-file-info') }}
      </p>
    </li>
  </ul>
  <p v-if="filesCount === 0" class="mb-16 text-center">No images to display.</p>
</template>

<script setup lang="ts">
import { Icon, IconName, IconSize } from '@exfluential/ui';
import { computed, ref } from 'vue';
import { ContentSocialFileDto } from '@/api/contents';
import { SocialPostPlatform } from '~/pages/content/my-posts/my-posts.constants';
import { TextArea } from '~/components/inputs/text-area';
import { isFileVideo } from '~/utils/file';
import { maxLength } from '~/utils/validators';
import { MAX_CAPTION_LENGTH } from '~/pages/content/my-posts/_components/post-modal-content/_components/media-manager/media-manager.constants';

type Props = {
  socialType: SocialPostPlatform;
  allowVideos?: boolean;
  noCaption?: boolean;
  isFileEditable?: boolean;
};

defineProps<Props>();

const files = defineModel<ContentSocialFileDto[]>({
  required: true,
});

const dragIndex = ref<number | null>(null);

const maxLengthLimit = maxLength(MAX_CAPTION_LENGTH);

const filesCount = computed(() => {
  return files.value.length;
});

const deleteFile = (id: number) => {
  files.value = files.value.filter((file) => file.file.id !== id);
};

const onDrop = (targetIndex: number) => {
  if (dragIndex.value === null || dragIndex.value === targetIndex) {
    dragIndex.value = null;
    return;
  }
  const reordered = [...files.value];
  const [moved] = reordered.splice(dragIndex.value, 1);
  if (moved) {
    reordered.splice(targetIndex, 0, moved);
    files.value = reordered;
  }
  dragIndex.value = null;
};
</script>
